import React, { useCallback, useEffect, useMemo } from "react";

import { differenceInHours } from "date-fns";
import { useSessionStorage } from "usehooks-ts";

import {
  useGetEagerNodeExecutions,
  useGetNodeExecutions,
} from "src/api/testRuns/testRunQueries";
import { ErroredRow } from "src/api/types";
import { FixedPositionedDropdownElement } from "src/base-components/FixedPositionedDropDown";
import { DecisionEnvironment } from "src/clients/history_v3";
import { DecisionEnvironment as JobRunDecisionEnvironment } from "src/clients/jobs";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { useSelectedDatasetId } from "src/datasets/store/useSelectedDatasetId";
import {
  getIntermediateResultColumns,
  StatusColumnProps,
} from "src/dataTable/TableUtils";
import { Callout } from "src/design-system/Callout";
import { useEntitySchemas } from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { EnvAgnosticFileLookupProvider } from "src/files/EnvAgnosticFileLookupContext";
import { isJobRunCompleted } from "src/flowContainer/RunFlowButton/utils";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { JobRun } from "src/jobs/types";
import { IntermediateResultsTableV2 } from "src/nodeEditor/IntermediateResultsTableV2";
import {
  useDataset,
  useOutputNodeDecisions,
  useTimeFilter,
} from "src/nodeEditor/JobRunIntermediateResultTableWrapper/hooks";
import {
  CustomStatusFilterKey,
  toNodeExecutionStatusFilter,
  transformDecisionsToResultDataRows,
  transformNodeResultsToResultDataRows,
} from "src/nodeEditor/JobRunIntermediateResultTableWrapper/utils";
import { RowCountInfo } from "src/nodeEditor/RowCountInfo";
import { useJobRunNodeStatusCounts } from "src/nodeEditor/useJobRunNodeStatusCounts";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useSetEditDatasetId } from "src/router/SearchParams";
import { useGraphStore } from "src/store/StoreProvider";

type Props = {
  jobRun: JobRun;
  datasetId: string;
  showErrorInEditor: (error: ErroredRow) => void;
  openEditor: () => void;
  fieldNameToSourceNode?: Map<string, { id: string; type: NODE_TYPE }>;
};

export const getExpectedResultsCount = ({
  statusFilter,
  totalCount,
  successCount,
  failureCount,
  ignoredCount,
}: {
  statusFilter: CustomStatusFilterKey;
  totalCount: number;
  successCount: number;
  failureCount: number;
  ignoredCount: number;
}): number => {
  switch (statusFilter) {
    case "all":
      return totalCount;
    case "success":
      return successCount;
    case "failure":
      return failureCount;
    case "ignored":
      return ignoredCount;
    default:
      return 0;
  }
};

export const computeEffectiveHasNextDecisionsPage = ({
  hasNextDecisionsPage,
  expectedResultsCount,
  loadedCount,
}: {
  hasNextDecisionsPage: boolean;
  expectedResultsCount: number;
  loadedCount: number;
}): boolean => {
  if (!hasNextDecisionsPage) {
    return false;
  }

  if (expectedResultsCount > loadedCount) {
    return true;
  }

  return false;
};

export const JobRunIntermediateResultTableWrapper: React.FC<Props> = ({
  jobRun,
  datasetId,
  showErrorInEditor,
  openEditor,
  fieldNameToSourceNode,
}) => {
  const { workspace, flow } = useAuthoringContext();
  const featureGates = useWorkspaceFeatureGates();
  const { selectedDatasetId } = useSelectedDatasetId();
  const {
    lastRunDatasetId,
    selectedNode,
    setSelectedNode,
    nodes,
    displayedErroredRow,
  } = useGraphStore();
  const [environment] = useEnvironment();
  const isOutputNode = selectedNode?.type === NODE_TYPE.OUTPUT_NODE;
  const isHistorical = differenceInHours(new Date(), jobRun.updated_at) > 23;

  const { data } = useDataset(datasetId);

  const rowIdToIndex = useMemo(() => {
    return (
      data?.rows.reduce(
        (acc, row, index) => {
          acc[row.id] = index;
          return acc;
        },
        {} as Record<string, number>,
      ) ?? ({} as Record<string, number>)
    );
  }, [data?.rows]);

  const expectedOutputColumnNames = useMemo(
    () => data?.dataset.output_columns.map((column) => column.name) ?? [],
    [data?.dataset.output_columns],
  );

  const getNodeName = useCallback(
    (nodeId: string) => nodes.get(nodeId)?.data.label,
    [nodes],
  );

  const [statusFilter, setStatusFilter] =
    useSessionStorage<CustomStatusFilterKey>(
      `status-filter-${jobRun.id}-${selectedNode?.id}`,
      "all",
    );

  const {
    isJobRunning,
    successCount,
    failureCount,
    ignoredCount,
    outputMismatchCount,
    totalCount,
    isSkipped,
    isPolling: isPollingNodeCounts,
  } = useJobRunNodeStatusCounts(jobRun);

  const statusElements: FixedPositionedDropdownElement<
    string,
    CustomStatusFilterKey
  >[] = useMemo(
    () => [
      { key: "all", value: "All" },
      {
        key: "success",
        value: "Successful",
        disabled: successCount === 0,
      },
      {
        key: "failure",
        value: "Failed",
        disabled: failureCount === 0,
      },
      {
        key: "ignored",
        value: "Ignored",
        disabled: ignoredCount === 0 || isOutputNode,
      },
    ],
    [successCount, failureCount, ignoredCount, isOutputNode],
  );

  const statusFilterProps: StatusColumnProps = useMemo(
    () => ({
      isFiltering: statusFilter !== "all",
      onSelect: (key) => setStatusFilter(key as CustomStatusFilterKey),
      selected: statusFilter,
      elements: statusElements,
    }),
    [statusFilter, statusElements, setStatusFilter],
  );

  const { data: entitySchemas } = useEntitySchemas({
    baseUrl: workspace.base_url ?? "",
    options: {
      enabled: featureGates.entitiesEnabled,
    },
  });

  const expectedResultsCount = useMemo(
    () =>
      getExpectedResultsCount({
        statusFilter,
        totalCount,
        successCount,
        failureCount,
        ignoredCount,
      }),
    [totalCount, statusFilter, successCount, failureCount, ignoredCount],
  );

  // For a completed, unfiltered run prefer the eagerly-generated files; otherwise
  // fall back to the live endpoint (node_executions, or decisions for the output node).
  const wantsEagerResults =
    statusFilter === "all" && isJobRunCompleted(jobRun) && !isOutputNode;

  const { isManifestAvailable, ...eagerNodeResults } =
    useGetEagerNodeExecutions({
      baseUrl: workspace.base_url ?? "",
      nodeId: selectedNode?.id ?? "",
      jobRunId: jobRun.id,
      enabled: wantsEagerResults,
    });

  const useEagerResults = wantsEagerResults && eagerNodeResults.hasNodeResults;
  const timeFilter = useTimeFilter({
    jobRun,
    isHistorical,
    isMetricsWritten: isManifestAvailable,
  });

  const endpointNodeResults = useGetNodeExecutions({
    baseUrl: workspace.base_url ?? "",
    nodeId: selectedNode?.id ?? "",
    jobRunId: jobRun.id,
    flowId: flow.id,
    environment: DecisionEnvironment.TEST,
    ...timeFilter,
    pageSize: 100,
    shouldPoll: !isManifestAvailable,
    // Wait for the eager-file lookup to settle before falling back to live.
    enabled:
      !isOutputNode &&
      !useEagerResults &&
      (!wantsEagerResults || eagerNodeResults.urlsResolved),
    statuses: toNodeExecutionStatusFilter(statusFilter),
  });

  const {
    data: nodeResults,
    isFetching: isFetchingNodeResults,
    isFetchingNextPage: isFetchingNextNodeResultsPage,
    fetchNextPage: fetchNextNodeResultsPage,
    hasNextPage: hasNextNodeResultsPage,
    isPolling: isPollingNodeResults,
  } = (() => {
    if (useEagerResults && eagerNodeResults.data?.pages?.length) {
      return eagerNodeResults;
    }

    return endpointNodeResults;

    // useEagerResults ? eagerNodeResults : endpointNodeResults
  })();

  const {
    decisions,
    isFetchingDecisions,
    isFetchingNextDecisionsPage,
    hasNextDecisionsPage,
    isPollingDecisions,
    fetchNextDecisionsPage,
  } = useOutputNodeDecisions({
    jobRun,
    enabled: isOutputNode,
    timeFilter,
    statusFilter,
    expectedResultsCount,
    shouldPoll: !isManifestAvailable,
  });

  const transformedNodeResults = useMemo(() => {
    return transformNodeResultsToResultDataRows(nodeResults, rowIdToIndex);
  }, [nodeResults, rowIdToIndex]);

  const transformedDecisions = useMemo(() => {
    if (!isOutputNode)
      return { dataToDisplay: [], erroredRows: new Map<number, ErroredRow>() };
    return transformDecisionsToResultDataRows(
      decisions,
      rowIdToIndex,
      selectedNode?.id,
      expectedOutputColumnNames,
    );
  }, [
    decisions,
    isOutputNode,
    rowIdToIndex,
    selectedNode?.id,
    expectedOutputColumnNames,
  ]);

  const { dataToDisplay, erroredRows } = isOutputNode
    ? transformedDecisions
    : transformedNodeResults;

  useEffect(() => {
    if (displayedErroredRow) return;
    if (erroredRows.size === 0) return;

    const firstSchemaError = Array.from(erroredRows.values()).find((error) =>
      error.msg?.toString().toLocaleLowerCase().includes("schema"),
    );

    if (firstSchemaError) {
      if (
        firstSchemaError.node_id &&
        firstSchemaError.node_id !== selectedNode?.id
      ) {
        setSelectedNode(firstSchemaError.node_id);
      }
      showErrorInEditor(firstSchemaError);
    }
  }, [
    erroredRows,
    displayedErroredRow,
    showErrorInEditor,
    selectedNode?.id,
    setSelectedNode,
  ]);

  const { data: eventSchemas } = useEventSchemas();
  const columns = useMemo(() => {
    const dataColumns = new Set<string>();
    dataToDisplay.slice(0, 100).forEach((row) => {
      Object.keys(row.data).forEach((k) => dataColumns.add(k));
    });

    return getIntermediateResultColumns(
      Array.from(dataColumns),
      [],
      isOutputNode ? expectedOutputColumnNames : [],
      statusFilterProps,
      fieldNameToSourceNode,
      entitySchemas?.entities ?? [],
      eventSchemas ?? [],
      null,
      featureGates,
      environment,
    );
  }, [
    dataToDisplay,
    statusFilterProps,
    fieldNameToSourceNode,
    entitySchemas?.entities,
    eventSchemas,
    featureGates,
    environment,
    isOutputNode,
    expectedOutputColumnNames,
  ]);

  const onErrorClick = useCallback(
    (errorIndex: number) => {
      const clickedError = erroredRows.get(errorIndex);
      if (clickedError) {
        if (clickedError.node_id && clickedError.node_id !== selectedNode?.id) {
          setSelectedNode(clickedError.node_id);
        }
        showErrorInEditor(clickedError);
      }
    },
    [erroredRows, selectedNode?.id, setSelectedNode, showErrorInEditor],
  );

  const onIgnoredClick = useCallback(
    (ignoredIndex: number) => {
      const row = dataToDisplay.find((row) => row.index === ignoredIndex);

      if (row?.type !== "ignored") {
        return;
      }

      if (row.errorOriginNodeId !== undefined) {
        if (row.errorOriginNodeId !== selectedNode?.id) {
          setSelectedNode(row.errorOriginNodeId);
        }
        openEditor();
      }
    },
    [dataToDisplay, openEditor, selectedNode?.id, setSelectedNode],
  );

  const setEditDatasetId = useSetEditDatasetId();
  const onIgnoredTooltipClick = useCallback(() => {
    if (selectedDatasetId) {
      setEditDatasetId(selectedDatasetId);
    }
  }, [selectedDatasetId, setEditDatasetId]);

  const {
    isPolling,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = (() => {
    // While the eager files are still being probed the live hooks are disabled,
    // so surface the eager hook's loading state instead of an empty table.
    if (
      useEagerResults ||
      (wantsEagerResults && !eagerNodeResults.urlsResolved)
    ) {
      return {
        isPolling: eagerNodeResults.isPolling,
        isFetching: eagerNodeResults.isFetching,
        isFetchingNextPage: eagerNodeResults.isFetchingNextPage,
        hasNextPage: eagerNodeResults.hasNextPage,
        fetchNextPage: eagerNodeResults.fetchNextPage,
      };
    }
    if (isOutputNode) {
      return {
        isPolling: isPollingDecisions,
        isFetching: isFetchingDecisions,
        isFetchingNextPage: isFetchingNextDecisionsPage,
        hasNextPage: hasNextDecisionsPage,
        fetchNextPage: fetchNextDecisionsPage,
      };
    }
    return {
      isPolling: isPollingNodeResults,
      isFetching: isFetchingNodeResults,
      isFetchingNextPage: isFetchingNextNodeResultsPage,
      hasNextPage: hasNextNodeResultsPage,
      fetchNextPage: fetchNextNodeResultsPage,
    };
  })();

  if (!isOutputNode && isSkipped) {
    return <Callout variant="info">This Node was not run</Callout>;
  }

  const showWaiting = !isHistorical && !isManifestAvailable;

  return (
    <EnvAgnosticFileLookupProvider
      enabled={jobRun.environment === JobRunDecisionEnvironment.TEST}
    >
      <IntermediateResultsTableV2
        canFetchNextBatch={!isFetchingNextPage && !isFetching && hasNextPage}
        columns={columns}
        data={dataToDisplay}
        displayingOutputNode={selectedNode?.type === NODE_TYPE.OUTPUT_NODE}
        fetchNextBatch={fetchNextPage}
        getNodeName={getNodeName}
        isFetching={
          isFetching || isPolling || isPollingNodeCounts || showWaiting
        }
        lastRunDatasetID={lastRunDatasetId ?? undefined}
        nodeId={selectedNode?.id ?? ""}
        rowCountInfo={
          <>
            <RowCountInfo
              activeSuccessFilter="success"
              currentFilter={statusFilterProps.selected}
              failureCount={failureCount}
              ignoredCount={ignoredCount}
              successCount={successCount}
              mismatchCount={isOutputNode ? outputMismatchCount : undefined}
              disableIgnoredFilter={isOutputNode}
              onFilterChange={statusFilterProps.onSelect}
              showWaiting={isOutputNode && isJobRunning}
              disableMismatchFilter
              isSuccessExcludesMismatch
            />
            {showWaiting && (
              <div className="mt-3">
                <Callout variant="neutral">
                  <p>
                    Test run is still in progress. These results will continue
                    to change during the process.
                  </p>
                </Callout>
              </div>
            )}
          </>
        }
        statusFilter={statusFilter}
        testRunId={jobRun.id}
        onErrorClick={onErrorClick}
        onIgnoredClick={onIgnoredClick}
        onIgnoredTooltipClick={onIgnoredTooltipClick}
      />
    </EnvAgnosticFileLookupProvider>
  );
};
